import React, { useCallback, useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Modal } from './shared/Modal';
import { restaurantConfig } from '../config/restaurantConfig';
import AnimatedHamburger from './AnimatedHamburger';
import Drawer from './Drawer';
import { FaPhone } from 'react-icons/fa';

interface HeaderProps {
  language: string;
  toggleLanguage: () => void;
  handleLinkClick?: () => void;
  isMobileMenuOpen?: boolean;
  toggleMobileMenu?: () => void;
  isScrolled?: boolean;
}

export const Header = ({ language, toggleLanguage, handleLinkClick = () => {} }: HeaderProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [reservationModalIsOpen, setReservationModalIsOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const logoRef = useRef<HTMLImageElement>(null);
  const [logoVisible, setLogoVisible] = useState(false);
  const [mobileLogoVisible, setMobileLogoVisible] = useState(false);
  const [animationActive, setAnimationActive] = useState(false);
  const [heroLogoAnimationComplete, setHeroLogoAnimationComplete] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  // Add section observer to highlight active section in navbar
  useEffect(() => {
    if (isHomePage) {
      const sectionIds = ['home', 'about', 'menu', 'gallery', 'contact', 'booking'];
      
      const observerOptions = {
        root: null,
        rootMargin: '-20% 0px -80% 0px', // Consider section in view when it's 20% from the top
        threshold: 0
      };
      
      const observerCallback = (entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      };
      
      const observer = new IntersectionObserver(observerCallback, observerOptions);
      
      sectionIds.forEach(id => {
        const element = document.getElementById(id);
        if (element) observer.observe(element);
      });
      
      return () => {
        sectionIds.forEach(id => {
          const element = document.getElementById(id);
          if (element) observer.unobserve(element);
        });
      };
    }
  }, [isHomePage]);

  // Effect to handle hero logo animation
  useEffect(() => {
    if (isHomePage) {
      const heroLogo = document.getElementById('hero-logo');
      
      const handleHeroLogoAnimation = () => {
        if (heroLogo) {
          if (window.scrollY > 50) {
            if (!heroLogo.classList.contains('hero-logo-scroll')) {
              heroLogo.classList.add('hero-logo-scroll');
              setAnimationActive(true);
              
              // Set a timeout to mark animation as complete for mobile
              setTimeout(() => {
                setHeroLogoAnimationComplete(true);
                setMobileLogoVisible(true);
              }, 300); // Timing should match the CSS transition
            }
          } else {
            heroLogo.classList.remove('hero-logo-scroll');
            setAnimationActive(false);
            setHeroLogoAnimationComplete(false);
            setMobileLogoVisible(false);
          }
        }
      };
      
      window.addEventListener('scroll', handleHeroLogoAnimation);
      return () => {
        window.removeEventListener('scroll', handleHeroLogoAnimation);
      };
    } else {
      // On non-home pages, always show the mobile logo
      setMobileLogoVisible(true);
    }
  }, [isHomePage]);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      
      if (offset > 10) {
        if (!scrolled) {
          setScrolled(true);
          
          // When scrolled, show the navbar logo with precise timing
          if (isHomePage && !logoVisible) {
            setLogoVisible(true);
          }
        }
      } else {
        if (scrolled) {
          if (isHomePage) {
            setLogoVisible(false);
            setScrolled(false);
          } else {
            setScrolled(false);
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Set initial logo visibility based on page and scroll position
    const initialScrollPosition = window.scrollY > 10;
    setLogoVisible(!isHomePage || initialScrollPosition);
    setScrolled(initialScrollPosition);
    
    // Set initial mobile logo visibility
    if (!isHomePage) {
      setMobileLogoVisible(true);
    } else {
      setMobileLogoVisible(initialScrollPosition);
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHomePage, scrolled, logoVisible]);

  const toggleMobileMenu = () => {
    setMobileMenu(!mobileMenu);
  };

  const scrollToBookingSection = useCallback((e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    
    // Close menu if it's open
    if (mobileMenu) {
      setMobileMenu(false);
    }
    
    // Check if on homepage
    if (location.pathname !== '/') {
      // Navigate to homepage first, then scroll to booking section
      window.location.href = '/#booking';
    } else {
      // On homepage, just scroll to booking section
      const bookingSection = document.getElementById('booking');
      if (bookingSection) {
        bookingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [mobileMenu, location.pathname]);

  const toggleReservationModal = useCallback(() => {
    setReservationModalIsOpen(prevState => !prevState);
  }, []);

  const handleReservation = useCallback(() => {
    // Use the booking widget instead of the modal
    scrollToBookingSection();
  }, [scrollToBookingSection]);

  // Smooth scroll to contact section and close menu if open
  const scrollToContact = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    
    // Close menu if it's open
    if (mobileMenu) {
      setMobileMenu(false);
    }
    
    // Scroll to contact section
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  }, [mobileMenu]);

  // Handle link click and close menu
  const handleNavLinkClick = useCallback(() => {
    // Close the menu
    setMobileMenu(false);
    // Execute the parent handler function
    handleLinkClick();
  }, [handleLinkClick]);

  // For non-homepage, always show the logo
  // For homepage, only show when scrolled and with animation
  const shouldShowLogo = !isHomePage || logoVisible;

  const headerStyle = {
    backgroundColor: scrolled || !isHomePage || mobileMenu ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
    color: scrolled || !isHomePage || mobileMenu ? 'black' : 'white',
    boxShadow: scrolled || !isHomePage || mobileMenu ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
    transition: 'background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease',
  };

  const navLinkStyle = {
    color: scrolled || !isHomePage || mobileMenu ? '#333' : '#fff',
    transition: 'color 0.3s ease',
  };

  return (
    <header className={`fixed left-0 w-full z-50 transition-all duration-300 main-header ${scrolled || mobileMenu ? 'bg-white shadow-md py-3 scrolled header-scrolled' : 'bg-transparent py-4 header-transparent'}`} style={{ top: '0' }}>
      {/* Custom CSS to enforce styling */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* Hide ONLY hamburger menu on desktop with more specificity */
          @media (min-width: 1024px) {
            .mobile-hamburger,
            .mobile-hamburger *,
            .mobile-hamburger *:before,
            .mobile-hamburger *:after,
            header .mobile-hamburger,
            div.mobile-hamburger {
              display: none !important;
              opacity: 0 !important;
              visibility: hidden !important;
              width: 0 !important;
              height: 0 !important;
              overflow: hidden !important;
              position: absolute !important;
              pointer-events: none !important;
              max-width: 0 !important;
              max-height: 0 !important;
            }
          }
          
          /* Hide all language-switcher-fixed elements to prevent duplicates */
          .language-switcher-fixed:not(.primary-flags) {
            display: none !important;
            opacity: 0 !important;
            visibility: hidden !important;
            width: 0 !important;
            height: 0 !important;
          }
          
          /* Style for all navigation items with transition */
          .navigation li a {
            position: relative;
            transition: color 0.3s ease;
          }
          
          .navigation li a::after {
            content: '';
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform: translateX(-50%) scale(0);
            width: 6px;
            height: 6px;
            background-color: var(--color-pink);
            border-radius: 50%;
            opacity: 0;
            transition: transform 0.3s ease, opacity 0.3s ease;
          }
          
          /* Style for active navigation item */
          .navigation li.active a {
            color: var(--color-pink) !important;
          }
          
          .navigation li.active a::after {
            transform: translateX(-50%) scale(1);
            opacity: 1;
          }
          
          /* Make nav text actually bold */
          .nav-link {
            font-weight: 700 !important;
          }
          
          /* Mobile navbar improvements */
          @media (max-width: 1023px) {
            .main-header .container {
              padding-left: 1rem;
              padding-right: 1rem;
            }
            
            /* Perfect vertical alignment for mobile navbar */
            .main-header {
              display: flex;
              align-items: center;
            }
            
            .main-header > div {
              width: 100%;
            }
            
            /* Improve mobile button styling */
            .main-header .bg-\\[\\#cb6e92\\] {
              height: 2.5rem;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: 600;
              letter-spacing: 0.025em;
            }
            
            /* Better hamburger menu positioning */
            .main-header button[aria-label*="menu"] {
              height: 2.5rem;
              width: 2.5rem;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }
        `
      }} />
      
      {/* Main container with fixed width and proper centering */}
      <div className="relative mx-auto w-full max-w-7xl px-4">
        {/* Navigation wrapper with improved mobile layout */}
        <div className="flex items-center justify-between h-16 lg:h-auto">
          {/* Empty space on the left to balance the flags on the right */}
          <div className="lg:w-48 hidden lg:block"></div>
          
          {/* Mobile Logo - Only visible when scrolled */}
          <div className="lg:hidden flex items-center justify-start flex-1 min-w-0 -ml-2">
            {scrolled && (
              <Link 
                to="/" 
                className="block"
                onClick={() => {
                  if (location.pathname === '/') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                <img 
                  src="/images/logo.png" 
                  alt="No Patio Logo" 
                  className="h-11 object-contain"
                  ref={logoRef}
                />
              </Link>
            )}
          </div>
          
          {/* Center navigation - take the available width */}
          <nav className="hidden lg:block">
            <ul className="navigation flex items-center gap-8">
              <li className={`${location.pathname === '/' ? 'current' : ''} ${activeSection === 'home' ? 'active' : ''}`}>
                <a 
                  href="#" 
                  className="nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    handleNavLinkClick();
                  }}
                >
                  {language === 'en' ? 'Home' : 'Início'}
                </a>
              </li>
              
              <li className={`${activeSection === 'booking' ? 'active' : ''}`}>
                <a 
                  href="#booking" 
                  className="nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (location.pathname !== '/') {
                      window.location.href = '/#booking';
                    } else {
                      const bookingSection = document.getElementById('booking');
                      if (bookingSection) {
                        bookingSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                    handleNavLinkClick();
                  }}
                >
                  {language === 'en' ? 'Reservations' : 'Reservas'}
                </a>
              </li>
              
              <li className={`${location.pathname === '/about' ? 'current' : ''} ${activeSection === 'about' ? 'active' : ''}`}>
                <a 
                  href="#about" 
                  className="nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (location.pathname !== '/') {
                      window.location.href = '/#about';
                    } else {
                      const aboutSection = document.getElementById('about');
                      if (aboutSection) {
                        aboutSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                    handleNavLinkClick();
                  }}
                >
                  {language === 'en' ? 'Our Story' : 'A Nossa História'}
                </a>
              </li>
              
              <li className={`${activeSection === 'menu' ? 'active' : ''}`}>
                <a 
                  href="#menu" 
                  className="nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (location.pathname !== '/') {
                      window.location.href = '/#menu';
                    } else {
                      const menuSection = document.getElementById('menu');
                      if (menuSection) {
                        menuSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                    handleNavLinkClick();
                  }}
                >
                  {language === 'en' ? 'Menu' : 'Menu'}
                </a>
              </li>
              
              <li className={`${location.pathname === '/gallery' ? 'current' : ''} ${activeSection === 'gallery' ? 'active' : ''}`}>
                <a 
                  href="#gallery" 
                  className="nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (location.pathname !== '/') {
                      window.location.href = '/#gallery';
                    } else {
                      const gallerySection = document.getElementById('gallery');
                      if (gallerySection) {
                        gallerySection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                    handleNavLinkClick();
                  }}
                >
                  {language === 'en' ? 'Gallery' : 'Galeria'}
                </a>
              </li>
              
              <li className={`${location.pathname === '/contact' ? 'current' : ''} ${activeSection === 'contact' ? 'active' : ''}`}>
                <a 
                  href="#contact" 
                  className="nav-link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (location.pathname !== '/') {
                      window.location.href = '/#contact';
                    } else {
                      const contactSection = document.getElementById('contact');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                  }}
                >
                  {language === 'en' ? 'Contact' : 'Contacto'}
                </a>
              </li>
              
              <li>
                <a 
                  href="tel:+351282763777"
                  className="bg-[#cb6e92] text-white px-5 py-2.5 rounded-full transition-all duration-300 hover:bg-opacity-90 hover:shadow-lg hover:-translate-y-1 font-bold inline-block hover:text-white"
                  style={{ 
                    color: 'white !important',
                    textDecoration: 'none !important'
                  }}
                >
                  <span style={{ color: 'white !important' }}>
                    {language === 'en' ? 'Book a Table' : 'Reservar Mesa'}
                  </span>
                </a>
              </li>
            </ul>
          </nav>
          
          {/* Mobile actions container - center */}
          <div className="lg:hidden flex items-center justify-center flex-shrink-0 ml-1">
            <a 
              href="tel:+351282763777"
              className="bg-[#cb6e92] text-white px-4 py-2 rounded-full text-sm font-bold transition-all duration-300 hover:bg-opacity-90 whitespace-nowrap shadow-sm flex items-center h-10"
            >
              <FaPhone className="w-3 h-3 mr-2" />
              {language === 'en' ? 'Book a Table' : 'Reservar Mesa'}
            </a>
          </div>
          
          {/* Mobile hamburger - right side */}
          <div className="lg:hidden flex items-center justify-end flex-1 min-w-0">
            <button
              onClick={toggleMobileMenu}
              className="flex flex-col justify-center items-center w-10 h-10 relative z-[9999] ml-3"
              aria-label={mobileMenu ? 'Close menu' : 'Open menu'}
            >
              <span className={`block w-6 h-0.5 rounded-full transition-all duration-300 mb-1 ${mobileMenu ? 'transform rotate-45 translate-y-1.5' : ''} ${scrolled || !isHomePage ? 'bg-gray-800' : 'bg-white'}`} style={{boxShadow: scrolled || !isHomePage ? 'none' : '0 1px 2px rgba(0,0,0,0.2)'}}></span>
              <span className={`block w-6 h-0.5 rounded-full transition-all duration-300 ${mobileMenu ? 'opacity-0' : 'opacity-100'} ${scrolled || !isHomePage ? 'bg-gray-800' : 'bg-white'}`} style={{boxShadow: scrolled || !isHomePage ? 'none' : '0 1px 2px rgba(0,0,0,0.2)'}}></span>
              <span className={`block w-6 h-0.5 rounded-full transition-all duration-300 mt-1 ${mobileMenu ? 'transform -rotate-45 -translate-y-1.5' : ''} ${scrolled || !isHomePage ? 'bg-gray-800' : 'bg-white'}`} style={{boxShadow: scrolled || !isHomePage ? 'none' : '0 1px 2px rgba(0,0,0,0.2)'}}></span>
            </button>
          </div>
          
          {/* Language flags fixed at the right */}
          <div className="hidden lg:flex lg:w-48 items-center justify-end">
            <button
              onClick={toggleLanguage}
              className={`language-btn ${language === 'pt' ? 'active opacity-100' : 'opacity-60'} mr-2 rounded-full overflow-hidden border-2 ${language === 'pt' ? 'border-pink-500' : 'border-transparent'} transition-all duration-300 hover:scale-110 hover:opacity-100`}
              aria-label="Portuguese"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
                <img src="/images/svg/Portugal.svg" alt="PT" className="w-full h-full object-cover" />
              </div>
            </button>
            <button
              onClick={toggleLanguage}
              className={`language-btn ${language === 'en' ? 'active opacity-100' : 'opacity-60'} rounded-full overflow-hidden border-2 ${language === 'en' ? 'border-pink-500' : 'border-transparent'} transition-all duration-300 hover:scale-110 hover:opacity-100`}
              aria-label="English"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
                <img src="/images/svg/Flag_of_Uk.svg" alt="EN" className="w-full h-full object-cover" />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Drawer Navigation for Mobile */}
      <Drawer 
        isOpen={mobileMenu} 
        onClose={() => setMobileMenu(false)} 
        logo="/images/logo.png"
        language={language}
        toggleLanguage={toggleLanguage}
        handleReservation={handleReservation}
      />

      {/* Custom CSS to ensure Book a Table button always has white text */}
      <style>{`
        .main-header .bg-\\[\\#cb6e92\\],
        .main-header.scrolled .bg-\\[\\#cb6e92\\],
        .main-header:not(.scrolled) .bg-\\[\\#cb6e92\\] {
          color: white !important;
        }
        .main-header .bg-\\[\\#cb6e92\\] span,
        .main-header.scrolled .bg-\\[\\#cb6e92\\] span,
        .main-header:not(.scrolled) .bg-\\[\\#cb6e92\\] span {
          color: white !important;
        }
        .main-header .bg-\\[\\#cb6e92\\]:hover,
        .main-header.scrolled .bg-\\[\\#cb6e92\\]:hover,
        .main-header:not(.scrolled) .bg-\\[\\#cb6e92\\]:hover {
          color: white !important;
        }
        .main-header .bg-\\[\\#cb6e92\\]:hover span,
        .main-header.scrolled .bg-\\[\\#cb6e92\\]:hover span,
        .main-header:not(.scrolled) .bg-\\[\\#cb6e92\\]:hover span {
          color: white !important;
        }
      `}</style>

      {/* Reservation Modal - We'll keep this for backward compatibility but it won't be used */}
      {reservationModalIsOpen && (
        <Modal 
          isOpen={reservationModalIsOpen} 
          onClose={toggleReservationModal}
          title={language === 'en' ? "Make a Reservation" : "Fazer uma Reserva"}
        >
          <div className="reservation-form">
            <p className="mb-4">
              {language === 'en' 
                ? "Please use our online booking system to make a reservation:"
                : "Por favor, use nosso sistema de reservas online para fazer uma reserva:"}
            </p>
            <button 
              onClick={() => {
                toggleReservationModal();
                scrollToBookingSection();
              }}
              className="w-full bg-[#C55E86] text-white py-3 rounded-md hover:bg-opacity-90 transition-all font-medium"
            >
              {language === 'en' ? 'Book Online' : 'Reservar Online'}
            </button>
          </div>
        </Modal>
      )}
    </header>
  );
}; 